(() => {
  const GLOBAL_KEY = '__CGCCartDrawerCrossSells';

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getFetchConfig() {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    return config;
  }

  function moneyFormat(price) {
    if (typeof price === 'string') return price;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format((Number(price) || 0) / 100);
  }

  class CartDrawerCrossSells extends HTMLElement {
    constructor() {
      super();
      this.config = null;
      this.loadingConfig = null;
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.stage = this.querySelector('[data-cart-drawer-cross-sells-root]');
      this.configNode = this.querySelector('[data-cart-drawer-cross-sells-config]');
      this.addEventListener('click', this.handleClick);
      this.onDesignModeSectionLoad = this.onDesignModeSectionLoad.bind(this);
      this.onDesignModeSectionSelect = this.onDesignModeSectionSelect.bind(this);

      if (Shopify.designMode) {
        document.addEventListener('shopify:section:load', this.onDesignModeSectionLoad);
        document.addEventListener('shopify:section:select', this.onDesignModeSectionSelect);
      }

      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:load', this.onDesignModeSectionLoad);
        document.removeEventListener('shopify:section:select', this.onDesignModeSectionSelect);
      }
    }

    async sync() {
      const config = await this.getConfig();
      if (!this.isConnected) return;

      if (!config || !Array.isArray(config.blocks) || config.blocks.length === 0) {
        this.hide();
        return;
      }

      const cartProductIds = this.getCartProductIds();
      const cartVariantIds = this.getCartVariantIds();
      const selectedBlock = this.selectBlock(config.blocks, cartProductIds, cartVariantIds);

      if (!selectedBlock || !Array.isArray(selectedBlock.products) || selectedBlock.products.length === 0) {
        this.hide();
        return;
      }

      this.render(selectedBlock, config.sectionId);
    }

    async getConfig() {
      if (this.config) return this.config;

      const cached = window[GLOBAL_KEY]?.config;
      if (cached) {
        this.config = cached;
        return cached;
      }

      const localConfig = this.readConfigFromDom();
      if (localConfig) {
        this.config = localConfig;
        window[GLOBAL_KEY] = window[GLOBAL_KEY] || {};
        window[GLOBAL_KEY].config = localConfig;
        return localConfig;
      }

      if (!this.loadingConfig) {
        this.loadingConfig = fetch(`${window.location.pathname}?section_id=cart-drawer`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsed = new DOMParser().parseFromString(responseText, 'text/html');
            const configNode = parsed.querySelector('[data-cart-drawer-cross-sells-config]');
            const config = configNode ? this.parseConfig(configNode.textContent || '') : null;
            if (config) {
              this.config = config;
              window[GLOBAL_KEY] = window[GLOBAL_KEY] || {};
              window[GLOBAL_KEY].config = config;
            }
            return config;
          })
          .catch((error) => {
            console.error('Error loading cart drawer cross-sells config:', error);
            return null;
          })
          .finally(() => {
            this.loadingConfig = null;
          });
      }

      return this.loadingConfig;
    }

    readConfigFromDom() {
      if (!this.configNode) return null;
      return this.parseConfig(this.configNode.textContent || '');
    }

    parseConfig(rawConfig) {
      const trimmed = rawConfig.trim();
      if (!trimmed) return null;

      try {
        return JSON.parse(trimmed);
      } catch (error) {
        console.error('Error parsing cart drawer cross-sells config:', error);
        return null;
      }
    }

    getCartProductIds() {
      return Array.from(document.querySelectorAll('[data-cart-product-id]'))
        .map((element) => element.dataset.cartProductId)
        .filter(Boolean);
    }

    getCartVariantIds() {
      return Array.from(document.querySelectorAll('[data-cart-variant-id]'))
        .map((element) => element.dataset.cartVariantId)
        .filter(Boolean);
    }

    selectBlock(blocks, cartProductIds, cartVariantIds) {
      let fallbackBlock = null;
      const cartProductIdSet = new Set(cartProductIds.map((id) => String(id)));
      const cartVariantIdSet = new Set(cartVariantIds.map((id) => String(id)));

      for (const block of blocks) {
        const triggerProductIds = Array.isArray(block.triggerProductIds) ? block.triggerProductIds : [];
        const triggerVariantIds = this.getTriggerVariantIds(block);
        const filteredProducts = this.filterProductsAlreadyInCart(block.products, cartProductIdSet, cartVariantIdSet);
        const renderableBlock = {
          ...block,
          products: filteredProducts,
        };

        if (
          triggerProductIds.length === 0 &&
          triggerVariantIds.length === 0 &&
          !fallbackBlock &&
          filteredProducts.length
        ) {
          fallbackBlock = renderableBlock;
        }

        const matchesProduct = triggerProductIds.some((triggerProductId) =>
          cartProductIds.includes(String(triggerProductId)),
        );
        const matchesVariant = triggerVariantIds.some((triggerVariantId) =>
          cartVariantIds.includes(String(triggerVariantId)),
        );

        if ((matchesProduct || matchesVariant) && filteredProducts.length) {
          return renderableBlock;
        }
      }

      return fallbackBlock;
    }

    filterProductsAlreadyInCart(products, cartProductIdSet, cartVariantIdSet) {
      if (!Array.isArray(products)) return [];

      return products.filter((product) => {
        const productId = String(product.id);
        const variantId = String(product.variantId);

        return !cartProductIdSet.has(productId) && !cartVariantIdSet.has(variantId);
      });
    }

    getTriggerVariantIds(block) {
      const rawIds = String(block.triggerVariantIdsRaw || '')
        .split(/[\n,]/)
        .map((id) => id.trim())
        .filter(Boolean);

      return rawIds;
    }

    render(block, sectionId) {
      if (!this.stage) return;

      const resolvedSectionId = sectionId || 'cart-drawer-cross-sells';
      const sliderId = `Slider-${resolvedSectionId}-${block.id}`;

      this.stage.innerHTML = this.buildMarkup(block, resolvedSectionId);
      this.hidden = false;
      this.removeAttribute('hidden');
      this.initializeDotPagination(sliderId);
    }

    buildMarkup(block, sectionId) {
      const sliderId = `Slider-${sectionId}-${block.id}`;
      const orientationClass =
        block.orientation === 'vertical'
          ? 'cart-drawer-cross-sells__list--vertical'
          : 'cart-drawer-cross-sells__list--horizontal';
      const hasMultipleProducts = block.products.length > 1;
      const slides = block.products
        .map((product, index) => this.buildProductCard(product, block.orientation, sectionId, index + 1))
        .join('');

      return `
        <div class="cart-drawer-cross-sells__inner">
          ${block.heading ? `<h3 class="cart-drawer-cross-sells__heading">${this.escapeHtml(block.heading)}</h3>` : ''}
          <slider-component class="slider-mobile-gutter cart-drawer-cross-sells__slider">
            <ul
              id="${sliderId}"
              class="cart-drawer-cross-sells__list ${orientationClass} slider slider--desktop slider--tablet grid--peek"
              role="list"
              aria-label="${this.escapeHtml(block.heading || 'Cross-sell products')}"
            >
              ${slides}
            </ul>
            ${hasMultipleProducts ? this.buildSliderDots(sliderId) : ''}
          </slider-component>
        </div>
      `;
    }

    buildSliderDots(sliderId) {
      return `
        <div class="slider-buttons cart-drawer-cross-sells__buttons">
          <div class="slideshow__control-wrapper" data-cross-sell-dots-for="${sliderId}"></div>
        </div>
      `;
    }

    initializeDotPagination(sliderId) {
      if (!this.stage) return;

      const slider = this.stage.querySelector(`[id="${sliderId}"]`);
      const dotsContainer = this.stage.querySelector(`[data-cross-sell-dots-for="${sliderId}"]`);
      if (!slider || !dotsContainer) return;

      const slides = Array.from(slider.querySelectorAll('[id^="Slide-"]'));
      if (slides.length === 0) return;

      const maxScrollLeft = Math.max(0, slider.scrollWidth - slider.clientWidth);
      const scrollPositions = Array.from(
        new Set(
          slides.map((slide) => {
            const clampedOffset = Math.min(slide.offsetLeft, maxScrollLeft);
            return Math.max(0, Math.round(clampedOffset));
          }),
        ),
      );

      if (scrollPositions.length <= 1) {
        const buttonsWrapper = dotsContainer.closest('.cart-drawer-cross-sells__buttons');
        if (buttonsWrapper) buttonsWrapper.setAttribute('hidden', 'hidden');
        return;
      }

      dotsContainer.innerHTML = scrollPositions
        .map((position, index) => {
          const slideNumber = index + 1;
          return `
            <button
              class="slider-counter__link slider-counter__link--dots link${index === 0 ? ' slider-counter__link--active' : ''}"
              aria-label="Go to slide ${slideNumber} of ${scrollPositions.length}"
              aria-controls="${sliderId}"
              aria-current="${index === 0 ? 'true' : 'false'}"
              data-cross-sell-dot-position="${position}"
            >
              <span class="dot"></span>
            </button>
          `;
        })
        .join('');

      const dotButtons = Array.from(dotsContainer.querySelectorAll('[data-cross-sell-dot-position]'));
      if (dotButtons.length === 0) return;

      const setActiveDot = (activeIndex) => {
        dotButtons.forEach((button, index) => {
          const isActive = index === activeIndex;
          button.classList.toggle('slider-counter__link--active', isActive);
          button.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      };

      const getActiveIndex = () => {
        let activeIndex = 0;
        let smallestDistance = Number.POSITIVE_INFINITY;

        scrollPositions.forEach((position, index) => {
          const distance = Math.abs(slider.scrollLeft - position);
          if (distance < smallestDistance) {
            smallestDistance = distance;
            activeIndex = index;
          }
        });

        return activeIndex;
      };

      slider.addEventListener(
        'scroll',
        () => {
          setActiveDot(getActiveIndex());
        },
        { passive: true },
      );

      dotButtons.forEach((button, index) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const targetPosition = Number(button.dataset.crossSellDotPosition);
          if (Number.isNaN(targetPosition)) return;

          slider.scrollTo({
            left: targetPosition,
            behavior: 'smooth',
          });
        });
      });

      setActiveDot(getActiveIndex());
    }

    buildProductCard(product, orientation, sectionId, slideIndex) {
      const titleParts = String(product.title || '')
        .split('|')
        .map((part) => part.trim())
        .filter(Boolean);
      const mainTitle = titleParts[0] || '';
      const subtitle = titleParts.slice(1).join(' | ');
      const isHorizontal = orientation === 'horizontal';
      const addLabel = product.variantCount > 1 ? 'View product' : product.available ? 'Add to basket' : 'Sold out';
      const cardClass = isHorizontal
        ? 'cart-drawer-cross-sells__card cart-drawer-cross-sells__card--horizontal'
        : 'cart-drawer-cross-sells__card cart-drawer-cross-sells__card--vertical';
      const slideClass = isHorizontal
        ? 'slider__slide cart-drawer-cross-sells__slide cart-drawer-cross-sells__slide--horizontal grid__item'
        : 'slider__slide cart-drawer-cross-sells__slide cart-drawer-cross-sells__slide--vertical grid__item';

      return `
        <li
          id="Slide-${sectionId}-${slideIndex}"
          class="${slideClass}"
          role="group"
        >
          <article class="${cardClass}">
            ${this.buildProductImage(product, mainTitle, isHorizontal)}
            <div class="cart-drawer-cross-sells__content">
              <a class="cart-drawer-cross-sells__title-link" href="${this.escapeAttribute(product.url)}">
                <h4 class="cart-drawer-cross-sells__title">${this.escapeHtml(mainTitle)}</h4>
              </a>
              ${subtitle ? `<p class="cart-drawer-cross-sells__subtitle">${this.escapeHtml(subtitle)}</p>` : ''}
              <p class="cart-drawer-cross-sells__price">${this.escapeHtml(product.price || moneyFormat(product.price))}</p>
              ${this.buildAction(product, addLabel)}
            </div>
          </article>
        </li>
      `;
    }

    buildProductImage(product, alt, isHorizontal) {
      if (!product.imageUrl) {
        return '';
      }

      const mediaBackgroundStyle = product.mediaBackground
        ? ` style="background-color: ${this.escapeAttribute(product.mediaBackground)};"`
        : '';

      return `
        <div class="cart-drawer-cross-sells__media${isHorizontal ? ' cart-drawer-cross-sells__media--horizontal' : ''}"${mediaBackgroundStyle}>
          <img
            class="cart-drawer-cross-sells__image"
            src="${this.escapeAttribute(product.imageUrl)}"
            alt="${this.escapeAttribute(product.imageAlt || alt)}"
            loading="lazy"
          >
        </div>
      `;
    }

    buildAction(product, label) {
      if (product.variantCount > 1) {
        return `
          <a class="button button--primary cart-drawer-cross-sells__button" href="${this.escapeAttribute(product.url)}">
            ${this.escapeHtml(label)}
          </a>
        `;
      }

      if (!product.available) {
        return `
          <button class="button button--primary cart-drawer-cross-sells__button" type="button" disabled>
            ${this.escapeHtml(label)}
          </button>
        `;
      }

      return `
        <button
          class="button button--primary cart-drawer-cross-sells__button"
          type="button"
          data-cart-drawer-cross-sell-add
          data-variant-id="${this.escapeAttribute(product.variantId)}"
          data-product-title="${this.escapeAttribute(product.title)}"
          data-product-url="${this.escapeAttribute(product.url)}"
        >
          <span class="cart-drawer-cross-sells__button-text">${this.escapeHtml(label)}</span>
          <span class="cart-drawer-cross-sells__spinner hidden" aria-hidden="true"></span>
        </button>
      `;
    }

    async handleClick(event) {
      const button = event.target.closest('[data-cart-drawer-cross-sell-add]');
      if (!button || !this.contains(button)) return;

      event.preventDefault();

      const variantId = button.dataset.variantId;
      if (!variantId) return;

      button.setAttribute('disabled', 'disabled');
      button.classList.add('loading');

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('properties[_cross_sell_source]', 'cart_drawer');

        const cartDrawer = getCartDrawer();
        if (cartDrawer) {
          formData.append(
            'sections',
            cartDrawer.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          cartDrawer.setActiveElement(document.activeElement);
        }

        const config = getFetchConfig();
        config.body = formData;

        const response = await fetch(`${routes.cart_add_url}`, config);
        const parsedResponse = await response.json();

        if (parsedResponse.status) {
          console.error('Error adding cross-sell to cart:', parsedResponse.description || parsedResponse.message);
          return;
        }

        if (cartDrawer) {
          cartDrawer.renderContents(parsedResponse);
        }

        this.fireSiteInteractionEvent(
          'cross_sell_add_to_cart',
          button.dataset.productTitle || button.dataset.variantId,
        );
      } catch (error) {
        console.error('Error adding cross-sell to cart:', error);
      } finally {
        button.classList.remove('loading');
        button.removeAttribute('disabled');
      }
    }

    hide() {
      this.hidden = true;
      this.setAttribute('hidden', 'hidden');
      if (this.stage) this.stage.innerHTML = '';
    }

    onDesignModeSectionLoad(event) {
      if (!event.target?.querySelector?.('cart-drawer-cross-sells')) return;
      this.sync();
    }

    onDesignModeSectionSelect(event) {
      const isCartDrawerSection = event.target?.querySelector?.('cart-drawer-cross-sells');
      if (!isCartDrawerSection) return;

      this.sync();

      const cartDrawer = getCartDrawer();
      if (cartDrawer) {
        cartDrawer.open();
      }
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }

    fireSiteInteractionEvent(action, value) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'site_interaction',
        context: 'cart_cross_sell',
        action,
        value,
      });
    }
  }

  if (!customElements.get('cart-drawer-cross-sells')) {
    customElements.define('cart-drawer-cross-sells', CartDrawerCrossSells);
  }

  window[GLOBAL_KEY] = window[GLOBAL_KEY] || {};
  window[GLOBAL_KEY].sync = () => {
    const crossSells = document.querySelector('cart-drawer-cross-sells');
    if (crossSells) crossSells.sync();
  };

  window.CGCCartDrawerCrossSells = window[GLOBAL_KEY];
})();
